import { useState, useEffect } from "react";
import styled from "styled-components";
import { Text } from "../../components/Text";
import { Flex } from "../../components/Flex";
import { Input, FormInput } from "../../components/FormInput";
import axios from "axios";
import Loader from "../../components/Loader";
import Button from "../../components/Button";
import Modal from "../../components/Modal";
import { PanelHeader } from "../../components/Panel";
import { Box } from "../../components/Box";
import { FaTimes } from "react-icons/fa";
import { useForm } from "react-hook-form";
import { useSettings } from "../../Settings";
import { space, SpaceProps } from "styled-system";
import { AccountUpdateSpec, Account, ApiKeyResponse } from "@aika/types";
import { useUser } from "auth";

const SettingsLabel = styled.label`
  width: 150px;
  display: block;
  font-weight: 600;
`;

const LinkButton = styled.button`
  background: none !important;
  border: none;
  padding: 0 !important;
  color: ${(props) => props.theme.colors.primary};
  cursor: pointer;
  display: inline-flex;
  align-items: center;

  &:hover {
    text-decoration: underline;
  }
`;

const EditableValue = ({
  value,
  onConfirm,
}: {
  value: string;
  onConfirm: (val: string) => void;
}) => {
  const [editing, setEditing] = useState<boolean>(false);
  const [tempValue, setTempValue] = useState<string>(value);

  const confirm = () => {
    console.log(tempValue);
    onConfirm(tempValue);
    setEditing(false);
  };

  if (!editing) {
    return (
      <Flex>
        <Text mr={2}>{value}</Text>
        <LinkButton
          onClick={() => {
            setTempValue(value);
            setEditing(true);
          }}
        >
          Change
        </LinkButton>
      </Flex>
    );
  } else {
    return (
      <form onSubmit={confirm}>
        <Flex>
          <Input
            value={tempValue}
            onChange={(ev) => setTempValue(ev.target.value)}
            autoFocus={true}
            mr={2}
          />
          <LinkButton type="submit">Confirm</LinkButton>
        </Flex>
      </form>
    );
  }
};

type ChangePasswordForm = {
  password: string;
  confirmPassword: string;
};

const ChangePasswordModal = ({
  onClose,
  onCommit,
}: {
  onClose: () => void;
  onCommit: (newPassword: string) => void;
}) => {
  const { register, handleSubmit, errors } = useForm<ChangePasswordForm>();

  const onSubmit = (values: ChangePasswordForm) => {
    onCommit(values.password);
  };

  return (
    <Modal width="400px" onClose={onClose} p={0}>
      <PanelHeader>
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontSize={3} fontWeight={600}>
            Change Password
          </Text>
          <LinkButton onClick={onClose}>
            <FaTimes />
          </LinkButton>
        </Flex>
      </PanelHeader>
      <Box p={3}>
        <Text mb={3}>Enter your new password below:</Text>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormInput
            name="password"
            label="New Password"
            type="password"
            error={errors.password}
            ref={register({
              required: true,
              minLength: 8,
            })}
          />
          <Text mb={3} fontSize={2} color="grey3">
            Your new password must be at least 8 characters in length
          </Text>
          <Flex justifyContent="flex-end">
            <Button type="submit">Submit</Button>
          </Flex>
        </form>
      </Box>
    </Modal>
  );
};

const Well = styled.span<SpaceProps>`
  background: ${(props) => props.theme.colors.grey1};
  padding: 6px 12px;
  border-radius: 6px;
  box-shadow: inset 1px 1px 3px rgba(0, 0, 0, 0.2);
  border: 1px solid ${(props) => props.theme.colors.grey2};
  ${space};
`;

const AccountSettings = () => {
  const auth = useUser();
  const [profile, setProfile] = useState<Account>(null!);
  const [editingPassword, setEditingPassword] = useState<boolean>(false);
  const [settings, setSettings] = useSettings();

  useEffect(() => {
    axios.get("/api/account").then(({ data }) => setProfile(data));
  }, []);

  const saveProfile = async (spec: AccountUpdateSpec) => {
    try {
      const { data } = await axios.put("/api/account", spec);
      setProfile(data);
    } catch (err) {
      if (err.response.status === 409) {
        window.alert("That email address is taken");
      }
    }
  };

  const changePassword = async (newPassword: string) => {
    setEditingPassword(false);
    try {
      const spec: AccountUpdateSpec = {
        ...profile,
        password: newPassword,
      };
      await axios.put("/api/account", spec);
    } catch (err) {
      window.alert("There was an error");
    }
  };

  const genKey = () => {
    axios.post<ApiKeyResponse>("/api/settings/api-key").then(({ data }) => {
      setSettings({
        ...settings,
        apiKey: data.apiKey,
      });
    });
  };

  if (profile == null) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  const setProfileValue =
    (key: keyof Account) => (ev: React.ChangeEvent<HTMLInputElement>) => {
      setProfile({
        ...profile,
        [key]: ev.target.value,
      });
    };

  const deleteAccount = async () => {
    if (
      window.prompt(
        "This is permanent and 100% unreversible.\n\nPlease type 'delete me' into the box below to continue"
      )
    ) {
      await axios.delete("/api/account");
      auth.logout();
    }
  };

  return (
    <div>
      <Text fontWeight={600} fontSize={4} mb={3}>
        Account Settings
      </Text>
      <Flex alignItems="center" mb={2}>
        <SettingsLabel>First Name</SettingsLabel>
        <Input
          value={profile.firstName}
          onChange={setProfileValue("firstName")}
          placeholder="Your first name..."
          onBlur={() => saveProfile(profile)}
          style={{ width: 250 }}
        />
      </Flex>
      <Flex alignItems="center" mb={2}>
        <SettingsLabel>Last Name</SettingsLabel>
        <Input
          value={profile.lastName}
          onChange={setProfileValue("lastName")}
          placeholder="Your last name..."
          style={{ width: 250 }}
          onBlur={() => saveProfile(profile)}
        />
      </Flex>
      <Flex alignItems="center" mb={2}>
        <SettingsLabel>Email</SettingsLabel>
        <EditableValue
          value={profile.email}
          onConfirm={(value) => {
            const p = { ...profile, email: value };
            setProfile(p);
            saveProfile(p);
          }}
        />
      </Flex>
      <Flex alignItems="center" mb={5}>
        <SettingsLabel>Password</SettingsLabel>
        <Button onClick={() => setEditingPassword(true)}>
          Change Password
        </Button>
      </Flex>
      <Text fontWeight={600} mb={2} fontSize={4}>
        API Key
      </Text>
      <Flex alignItems="center" mb={5}>
        <Well mr={2}>{settings.apiKey || "Non Assigned"}</Well>
        <Button onClick={genKey}>Issue new key</Button>
      </Flex>
      <Text fontWeight={600} mb={2} fontSize={4}>
        Account Deletion
      </Text>
      <Button onClick={deleteAccount} color="danger">
        Delete My Account
      </Button>

      {editingPassword && (
        <ChangePasswordModal
          onClose={() => setEditingPassword(false)}
          onCommit={changePassword}
        />
      )}
    </div>
  );
};

export default AccountSettings;
